import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listFlagStyles,
  saveFlagStyle,
  deleteFlagStyle,
  listSystemConfig,
  saveSystemConfig,
  deleteSystemConfig,
} from '../../api/system/system.api';
import { FLAG_COLOR_PRESETS, DEFAULT_FLAG_COLOR, flagBadgeStyle } from '../../libs/flagStyle';
import LoadingBlock from '../LoadingBlock';

/**
 * 시스템 설정(`/sysadmin/setting`) — 2026-08-06 신설.
 *
 * **시스템 전역 설정을 모으는 화면.** 지금은 두 가지를 담는다.
 *  ① 플래그 색상 (`flag_style`) — 종전에는 모든 플래그가 같은 보라색이라 구분이 안 됐다.
 *  ② 런타임 설정 (`system_config`) — 배포 없이 조정하는 key-value. 종전에는 **DB 를 직접 UPDATE** 해야 했다.
 *
 * 여기 없는 설정은 성격이 달라서 일부러 다른 곳에 둔 것이다:
 *  - 정가표(상품·단가) → `/sysadmin/price`  · 구독/플래그 부여/일회성비 → 고객사 단위 화면
 *  - 채널별 설정(세금계산서 발행 업체 등) → 고객사 상세
 * 같은 데이터를 두 곳에서 고칠 수 있으면 어느 쪽이 최신인지 알 수 없게 된다(A2 화면 재편 원칙).
 */

/** 코드가 읽는 `system_config` 키. 값이 없으면 이 기본값으로 동작한다(= 삭제하면 기본값 복귀). */
const KNOWN_CONFIG_KEYS = [
  { key: 'weather.base_hour', def: '8', desc: '날씨 자동화 기준 시각(시)' },
  { key: 'weather.check_hours', def: '12,13', desc: '예보 확인 시각(쉼표 구분, 시)' },
  { key: 'weather.mode', def: 'TWO_GATE', desc: '판정 모드(TWO_GATE 등)' },
  { key: 'weather.rain_min_mm', def: '6.0', desc: '강수량 하한(mm)' },
  { key: 'weather.pop_floor', def: '70', desc: '강수확률 하한(%)' },
  { key: 'weather.score_threshold', def: '10.0', desc: '종합 점수 임계값' },
  { key: 'weather.use_pop', def: 'true', desc: '강수확률 사용 여부' },
  { key: 'weather.use_snow', def: 'false', desc: '적설 판정 사용 여부' },
  { key: 'weather.snow_threshold_cm', def: '1.0', desc: '적설 임계(cm)' },
];

const errText = (e) => e?.response?.data?.data || e?.message || String(e ?? '알 수 없는 오류');

export default function SystemSettingAdmin() {
  const [msg, setMsg] = useState('');
  const [msgError, setMsgError] = useState(false);
  const [busy, setBusy] = useState(false);

  const notify = (text, isError = false) => { setMsg(text); setMsgError(isError); };

  /** 저장/삭제 공통 — 성공하면 다시 불러오고, 실패하면 사유를 그대로 보여준다. */
  const run = async (fn, okMsg, reload) => {
    setBusy(true);
    notify('');
    try {
      await fn();
      if (reload) await reload();
      if (okMsg) notify(okMsg);
    } catch (e) {
      notify(errText(e), true);
    } finally {
      setBusy(false);
    }
  };

  // ===== ① 플래그 색상 =====
  const [flags, setFlags] = useState([]);
  const [flagLoading, setFlagLoading] = useState(true);
  const [flagError, setFlagError] = useState('');
  // 편집 중 값(저장 전) — flagCode → { color, sortOrder }
  const [flagDraft, setFlagDraft] = useState({});

  const loadFlags = useCallback(async () => {
    setFlagLoading(true);
    setFlagError('');
    try {
      const resp = await listFlagStyles();
      setFlags(resp?.data ?? []);
      setFlagDraft({});
    } catch (e) {
      setFlags([]);
      setFlagError(errText(e));
    } finally {
      setFlagLoading(false);
    }
  }, []);

  useEffect(() => { loadFlags(); }, [loadFlags]);

  const draftOf = (f) => flagDraft[f.flagCode] ?? { color: f.color ?? '', sortOrder: f.sortOrder ?? 0 };
  const editFlag = (code, patch) =>
    setFlagDraft((prev) => ({ ...prev, [code]: { ...(prev[code] ?? {}), ...patch } }));

  const isFlagDirty = (f) => {
    const d = flagDraft[f.flagCode];
    if (!d) return false;
    return (d.color ?? '') !== (f.color ?? '') || Number(d.sortOrder ?? 0) !== Number(f.sortOrder ?? 0);
  };

  const doSaveFlag = (f) => {
    const d = draftOf(f);
    if (!d.color) { notify('색을 먼저 선택해 주세요.', true); return; }
    run(() => saveFlagStyle(f.flagCode, d.color, Number(d.sortOrder) || 0), `'${f.flagCode}' 색상 저장 완료`, loadFlags);
  };

  const doResetFlag = (f) => {
    if (!window.confirm(`'${f.flagCode}' 색상 설정을 지우고 기본색으로 되돌립니다.\n\n채널에 달린 플래그 자체는 그대로 유지됩니다.`)) return;
    run(() => deleteFlagStyle(f.flagCode), `'${f.flagCode}' 기본색으로 되돌림`, loadFlags);
  };

  // ===== ② 런타임 설정(system_config) =====
  const [configs, setConfigs] = useState([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState('');
  const [configDraft, setConfigDraft] = useState({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const loadConfigs = useCallback(async () => {
    setConfigLoading(true);
    setConfigError('');
    try {
      const resp = await listSystemConfig();
      setConfigs(resp?.data ?? []);
      setConfigDraft({});
    } catch (e) {
      setConfigs([]);
      setConfigError(errText(e));
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const cDraftOf = (c) => configDraft[c.configKey] ?? { configValue: c.configValue, description: c.description ?? '' };
  const editConfig = (key, patch) =>
    setConfigDraft((prev) => ({ ...prev, [key]: { ...(prev[key] ?? {}), ...patch } }));
  const isConfigDirty = (c) => {
    const d = configDraft[c.configKey];
    if (!d) return false;
    return d.configValue !== c.configValue || (d.description ?? '') !== (c.description ?? '');
  };

  const doSaveConfig = (c) => {
    const d = cDraftOf(c);
    run(() => saveSystemConfig(c.configKey, d.configValue, d.description || null), `'${c.configKey}' 저장 완료`, loadConfigs);
  };

  const doDeleteConfig = (c) => {
    if (!window.confirm(
      `'${c.configKey}' 설정을 삭제합니다.\n\n삭제하면 코드에 있는 기본값으로 동작합니다(기능이 꺼지는 것이 아닙니다).\n\n계속할까요?`
    )) return;
    run(() => deleteSystemConfig(c.configKey), `'${c.configKey}' 삭제 — 기본값으로 동작합니다`, loadConfigs);
  };

  const doAddConfig = () => {
    const key = newKey.trim();
    if (!key) { notify('설정 키를 입력해 주세요.', true); return; }
    run(async () => {
      await saveSystemConfig(key, newValue, newDesc || null);
      setNewKey(''); setNewValue(''); setNewDesc('');
    }, `'${key}' 추가 완료`, loadConfigs);
  };

  /** 코드가 읽지만 DB 에 아직 없는 키 — 추가하면 배포 없이 조정할 수 있다. */
  const unsetKnownKeys = useMemo(() => {
    const have = new Set(configs.map((c) => c.configKey));
    return KNOWN_CONFIG_KEYS.filter((k) => !have.has(k.key));
  }, [configs]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center flex-wrap gap-3">
        <h2 className="text-xl font-bold">설정</h2>
        <span className="text-xs text-gray-500">
          시스템 전역 설정입니다. 상품·단가는 ‘정가표’, 고객사별 설정은 ‘전체 고객 &gt; 고객사 상세’에서 관리합니다.
        </span>
      </div>

      {msg && (
        <div className={`text-sm px-3 py-2 rounded whitespace-pre-line ${
          msgError ? 'bg-red-50 border border-red-300 text-red-700 font-semibold' : 'bg-gray-100'
        }`}>
          {msgError ? `⚠️ ${msg}` : msg}
        </div>
      )}

      {/* ===== ① 플래그 색상 ===== */}
      <section className="border rounded-lg">
        <header className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-bold">플래그 색상</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            고객사에 달아 둔 플래그의 배지 색을 정합니다. 색을 지정하지 않은 플래그는 기본색으로 표시됩니다.
            플래그를 <b>추가·삭제하는 곳은 ‘고객사 상세’</b>이고, 여기서는 색만 정합니다.
          </p>
        </header>

        {(flagLoading || flagError) ? (
          <LoadingBlock error={flagError} text="플래그를 불러오는 중입니다…" />
        ) : flags.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-400">
            등록된 플래그가 없습니다. ‘고객사 상세’에서 플래그를 먼저 달아 주세요.
          </div>
        ) : (
          <div className="divide-y">
            {flags.map((f) => {
              const d = draftOf(f);
              const preview = d.color || DEFAULT_FLAG_COLOR;
              return (
                <div key={f.flagCode} className="px-4 py-3 flex flex-wrap items-center gap-3">
                  <div className="w-40 shrink-0">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold" style={flagBadgeStyle(preview)}>
                      {f.flagCode}
                    </span>
                  </div>

                  <div className="text-xs text-gray-500 w-24 shrink-0">
                    {f.channelCount > 0 ? `${f.channelCount}개 고객사` : '사용 중 아님'}
                  </div>

                  {/* 팔레트 */}
                  <div className="flex flex-wrap gap-1 flex-1 min-w-[240px]">
                    {FLAG_COLOR_PRESETS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        title={c}
                        onClick={() => editFlag(f.flagCode, { color: c })}
                        className={`w-5 h-5 rounded border ${
                          (d.color || '').toUpperCase() === c ? 'ring-2 ring-offset-1 ring-gray-700 border-gray-700' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>

                  {/* 직접 입력 + 정렬 */}
                  <input
                    className="border rounded px-2 py-1 text-xs w-24 font-mono"
                    placeholder="#4F46E5"
                    value={d.color ?? ''}
                    onChange={(e) => editFlag(f.flagCode, { color: e.target.value })}
                  />
                  <label className="text-xs text-gray-500 flex items-center gap-1">
                    순서
                    <input
                      type="number"
                      className="border rounded px-2 py-1 text-xs w-16"
                      value={d.sortOrder ?? 0}
                      onChange={(e) => editFlag(f.flagCode, { sortOrder: e.target.value })}
                    />
                  </label>

                  <button
                    className="text-xs px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40"
                    onClick={() => doSaveFlag(f)}
                    disabled={busy || !isFlagDirty(f)}
                  >
                    저장
                  </button>
                  {f.color && (
                    <button
                      className="text-xs underline text-gray-400"
                      onClick={() => doResetFlag(f)}
                      disabled={busy}
                    >
                      기본색
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ===== ② 런타임 설정 ===== */}
      <section className="border rounded-lg">
        <header className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-bold">런타임 설정 (system_config)</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            배포 없이 바꿀 수 있는 값입니다. <b>삭제 = 코드 기본값으로 되돌리기</b>(기능이 꺼지는 것이 아닙니다).
          </p>
          <p className="text-xs text-red-600 mt-1">
            ⚠️ 값이 그대로 보이는 화면입니다. <b>API 키·비밀번호 같은 비밀값은 절대 넣지 마세요</b>(비밀값은 서버 Secret 으로 관리).
            값 형식이 틀리면 저장은 되고 <b>실행 시 조용히 기본값으로 동작</b>합니다.
          </p>
        </header>

        {(configLoading || configError) ? (
          <LoadingBlock error={configError} text="설정을 불러오는 중입니다…" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left w-64">키</th>
                  <th className="px-3 py-2 text-left w-48">값</th>
                  <th className="px-3 py-2 text-left">설명</th>
                  <th className="px-3 py-2 text-left w-36">수정 시각 (KST)</th>
                  <th className="px-3 py-2 w-28">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {configs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-sm text-gray-400">
                      저장된 설정이 없습니다. 아래 목록에서 추가하면 코드 기본값 대신 이 값이 쓰입니다.
                    </td>
                  </tr>
                )}
                {configs.map((c) => {
                  const d = cDraftOf(c);
                  return (
                    <tr key={c.configKey}>
                      <td className="px-3 py-2 font-mono text-xs break-all">{c.configKey}</td>
                      <td className="px-3 py-2">
                        <input
                          className="border rounded px-2 py-1 text-xs w-full font-mono"
                          value={d.configValue}
                          onChange={(e) => editConfig(c.configKey, { configValue: e.target.value })}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className="border rounded px-2 py-1 text-xs w-full"
                          placeholder="이 설정이 무엇인지 적어 두세요"
                          value={d.description ?? ''}
                          onChange={(e) => editConfig(c.configKey, { description: e.target.value })}
                        />
                      </td>
                      {/* 서버가 KST `yyyy-MM-dd HH:mm` 로 내려준다(`toSeoulText`) — 여기서 변환하지 않는다. */}
                      <td className="px-3 py-2 text-xs text-gray-400">{c.updatedAt ?? '—'}</td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <button
                          className="text-xs px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40 mr-1"
                          onClick={() => doSaveConfig(c)}
                          disabled={busy || !isConfigDirty(c)}
                        >
                          저장
                        </button>
                        <button
                          className="text-xs underline text-gray-400"
                          onClick={() => doDeleteConfig(c)}
                          disabled={busy}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 신규 추가 */}
        <div className="px-4 py-3 border-t bg-gray-50 flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-500 flex flex-col gap-1">
            키
            <input className="border rounded px-2 py-1 text-xs w-64 font-mono" placeholder="weather.pop_floor"
                   value={newKey} onChange={(e) => setNewKey(e.target.value)} />
          </label>
          <label className="text-xs text-gray-500 flex flex-col gap-1">
            값
            <input className="border rounded px-2 py-1 text-xs w-40 font-mono" placeholder="70"
                   value={newValue} onChange={(e) => setNewValue(e.target.value)} />
          </label>
          <label className="text-xs text-gray-500 flex flex-col gap-1 flex-1 min-w-[200px]">
            설명
            <input className="border rounded px-2 py-1 text-xs w-full" placeholder="강수확률 하한(%)"
                   value={newDesc} onChange={(e) => setNewDesc(e.target.value)} />
          </label>
          <button className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white disabled:opacity-40"
                  onClick={doAddConfig} disabled={busy}>
            추가
          </button>
        </div>

        {/* 코드가 읽지만 아직 DB 에 없는 키 안내 — 클릭하면 입력칸을 채운다 */}
        {unsetKnownKeys.length > 0 && (
          <div className="px-4 py-3 border-t text-xs text-gray-500">
            <div className="mb-1">
              코드가 읽는 키 중 <b>아직 저장되지 않은 것</b>입니다(지금은 괄호 안 기본값으로 동작).
              누르면 위 입력칸이 채워집니다.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {unsetKnownKeys.map((k) => (
                <button
                  key={k.key}
                  className="px-2 py-0.5 rounded border bg-white hover:bg-gray-50 font-mono"
                  onClick={() => { setNewKey(k.key); setNewValue(k.def); setNewDesc(k.desc); }}
                  title={k.desc}
                >
                  {k.key} ({k.def})
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
